import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.isdud.com:8450/backend'

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
})

// Attach JWT token to every request automatically
api.interceptors.request.use((config) => {
  let token = ''
  // Check 'auth' key (auth store format: JSON object with token field)
  try {
    const authRaw = localStorage.getItem('auth')
    if (authRaw) {
      const authData = JSON.parse(authRaw)
      token = authData.token || ''
    }
  } catch (e) {
    // ignore
  }
  // Fallback: check 'token' key (LoginPage stores plain token string)
  if (!token) {
    token = localStorage.getItem('token') || ''
  }
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Alias for web_site compatibility
export const apiService = api

// ===== Types =====
export interface Lawyer {
  lawyer_id: string
  user_id?: string
  name: string
  name_ar?: string
  phone_number?: string
  phone?: string
  email?: string
  lawyer_image?: string
  country_code?: string
  firm_id?: string
  is_active?: boolean
  specialization?: string
  bar_association_id?: string
  bio?: string
  status?: string
  rating_avg?: number
  total_reviews?: number
  created_at?: string
}

export interface Banner {
  banner_id: string
  title: string
  subtitle: string
  description: string
  image_url: string
  page: string
  section: string
  is_active: boolean
  sort_order: number
}

export interface PracticeArea {
  id: number
  parent_id?: number
  name: string
  published?: boolean
  description?: string
  level?: number
  icon?: string
  image_url?: string
  category_id?: number
  created_at?: string
  updated_at?: string
  practices?: PracticeArea[]
}

export interface ContentTranslation {
  id: number
  content_id: number
  language_code: string
  title: string
  subtitle?: string
  content?: string
  image_url?: string
  button1_text?: string
  button1_route?: string
  button2_text?: string
  button2_route?: string
  created_at: string
  updated_at: string
}

export interface Content {
  id: number
  firm_id: string
  page_name: string
  section_name: string
  title: string
  subtitle?: string
  content?: string
  image_url?: string
  button1_text?: string
  button1_route?: string
  button2_text?: string
  button2_route?: string
  sort_order: number
  is_active: boolean
  created_at: string
  updated_at: string
  translations?: ContentTranslation[]
}

export interface Review {
  id: string
  lawyer_id?: string
  firm_id?: string
  client_id?: string
  rating: number
  comment: string
  is_visible: boolean
  created_at: string
  updated_at: string
}

export interface Firm {
  firm_id: string
  name: string
  description?: string
  address?: string
  phone?: string
  email?: string
  firm_image?: string
  logo_url?: string
  lawyer_count?: number
  rating_avg?: number
  specializations?: string[]
  facebook?: string
  twitter?: string
  linkedin?: string
  instagram?: string
  user_id?: string
  featured?: boolean
  is_active?: boolean
  created_at?: string
  updated_at?: string
  lawyers?: Lawyer[]
  banners?: Banner[]
  practice_areas?: PracticeArea[]
  contents?: Content[]
  review?: Review[]
}

// ===== API Functions =====
export async function getFirms() {
  return api.get<{ success: boolean; data: Firm[] }>('/api/v1/firms')
}

export async function getFirm(firmId: string) {
  console.log('📡 API: getFirm called with firmId:', firmId)
  console.log('📡 API: Full URL:', `${API_BASE_URL}/api/v1/firms/${firmId}`)
  return api.get<{ success: boolean; data: Firm }>(`/api/v1/firms/${firmId}`)
}

export async function getFirmByWebsite(website?: string) {
  // If website is not provided, use the current origin
  const origin = website || window.location.origin
  console.log('📡 API: getFirmByWebsite called with origin:', origin)
  console.log('📡 API: Full URL:', `${API_BASE_URL}/api/v1/firms/by-website?website=${origin}`)
  return api.get<{ success: boolean; data: Firm }>('/api/v1/firms/by-website', {
    params: { website: origin }
  })
}

export async function getFirmContents(firmId: string) {
  return api.get<{ success: boolean; data: Content[] }>(`/api/v1/contents/firm/${firmId}`)
}

export async function getFirmReviews(firmId: string) {
  return api.get<{ success: boolean; data: Review[] }>(`/api/v1/firms/${firmId}/reviews`)
}

// ===== Auth Functions =====
export async function login(email: string, password: string, firmId?: string) {
  return api.post<{ success: boolean; data: { token: string; user: any; employee?: any; firm?: any } }>('/api/v1/auth/login', { email, password, firm_id: firmId })
}

export async function register(userData: {
  name: string
  email: string
  phone: string
  password: string
  user_type: string
  firm_id?: string
  license_number?: string
  address?: string
}) {
  // Convert to FormData for multipart/form-data
  const formData = new FormData()
  formData.append('full_name', userData.name)
  formData.append('email', userData.email)
  formData.append('phone', userData.phone)
  formData.append('password', userData.password)
  formData.append('user_type', userData.user_type)
  if (userData.firm_id) formData.append('firm_id', userData.firm_id)
  if (userData.license_number) formData.append('license_number', userData.license_number)
  if (userData.address) formData.append('address', userData.address)
  
  return api.post<{ success: boolean; data: { success: boolean; data: { user: any; token: string } } }>('/api/v1/auth/register', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}

// ===== Helpers =====
export function getFirmLogoUrl(firmId: string, logoPath?: string): string {
  if (!logoPath) return ''
  if (logoPath.startsWith('http')) return logoPath
  return `${API_BASE_URL}/images/firms/${firmId}/${logoPath}`
}

export function getLawyerImage(imagePath?: string): string {
  if (!imagePath) return '/profile.png'
  if (imagePath.startsWith('http')) return imagePath
  return `${API_BASE_URL}/images/lawyers/${imagePath}`
}

export function getContentImageUrl(imageUrl: string, firmId?: string): string {
  if (!imageUrl) return ''
  if (imageUrl.startsWith('http')) return imageUrl
  return `${API_BASE_URL}/images/firms/${firmId || ''}/${imageUrl}`
}
